import { Utilities } from "@tylertech/shared-module";
import { KeyValuePair } from "@tylertech/tyler-api-types";
import
{
    CachedCodeLookupService,
    MetadataPropertyService,
    MetadataInputTypeHelperService,
    CacheablePropertyMetadata,
    MetadataService,
    MetadataInputSelectService,
    MetadataEventService,
    CellEditService,
} from "../../../../services";
import { AugmentedModelObject, MetadataKey, ValueChangedParms } from "../../../../interfaces";
import { MetadataKeyHelpers } from "../../../../utilities/MetadataKeyHelpers";
import { ArrayHelpers } from "../../../../utilities/ArrayHelpers";
import { ObjectHelpers } from "../../../../utilities/ObjectHelpers";
import { AfterViewInit, Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";

interface SelectPropertyMetadata extends CacheablePropertyMetadata
{
    InputType: "Select" | "MultiSelect" | "DualList";
}

const RECOMMENDED_THRESHOLD = 0;
const MAX_RECORDS_RENDERED = 20;
function areEquivalent<T>(a: T, b: T): boolean
{
    if (Utilities.IsNullOrUndefined(a) && Utilities.IsNullOrUndefined(b)) return true;
    return JSON.stringify(a) === JSON.stringify(b);
}

@Component({
    selector: "metadata-input-select",
    templateUrl: "./metadata-input-select.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputSelectComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: MetadataKey | MetadataKey[];

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: SelectPropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    dense: boolean;

    @Input()
    forCellEdit: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    index: number;

    @Input()
    compact: boolean;

    @Input()
    error: string;

    private isInitialized: boolean;

    private _loadingPromise: Promise<any>;

    /**
     * TODO: this could be replaced with a calculated property that tests if the `!_loadingPromise`
     */
    private selectLoading: boolean = false;

    _options: KeyValuePair<MetadataKey, string>[];

    private dependentValues: any[];

    private filterValues: any[];

    private lookupPropertyValues: KeyValuePair<MetadataKey, string>[];

    private constantFilter: string;

    private requiredPropertyValue: boolean;

    private _selected: MetadataKey | MetadataKey[];

    private _optionSearch: string = "";

    private _popupOpen: boolean = false;

    loadMoreCount: number = 1;

    /** Accessible in the template */ isViewOnly: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: MetadataKey | MetadataKey[];

    controlId: string;

    selectOnBlur: boolean = true;

    private _density: string;

    private readonly destructor$ = new Subject<void>();

    private scrollHandler = (event: Event) => this.infiniteScroll(event, this);

    constructor(
        public modelGroup: NgModelGroup,
        private readonly $metadataService: MetadataService,
        private readonly $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        readonly $metadataInputSelectService: MetadataInputSelectService,
        private readonly $metadataPropertyService: MetadataPropertyService,
        readonly $metadataEventService: MetadataEventService,
        private readonly $cachedCodeLookupService: CachedCodeLookupService,
        private $cellEditService: CellEditService,
        private readonly $keyHandlerService: KeyHandlerService,
        private readonly $securityService: SecurityService,
    )
    {
        this.controlId = Utilities.generateRandomGuid();
    }

    ngOnInit(): void
    {
        if (!this.propertyMetadata)
        {
            console.warn(
                `${MetadataInputSelectComponent.name}|${this.propertyMetadata.Name}| propertyMetadata was not bound!`
            );
        }
        if (this.forCellEdit)
            this.setRecalculateStatus();
        const self = this;
        this.unboundModel = Utilities.deepCopy(this.model);
        this.isViewOnly = true;
        if (Utilities.IsNullOrUndefined(this.propertyMetadata.LookupProperty))
        {
            this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntil(this.destructor$))
                .subscribe((type) =>
                {
                    if (type === this.propertyMetadata.Lookup)
                        self.forceLoadSelect();
                });
        }
        this.loadSelect()
            .then(_x =>
            {
                // if (!Utilities.IsNullOrUndefined(this._options)
                // 	&& this._options.length === 1) {
                // 	//this.selected = this._options[0].Key;
                // }
            })
            .finally(() =>
            {
                if (this.propertyMetadata.InputType === "DualList")
                {
                    const dualListSelectListCard = document.querySelector(`#${this.dualListSelectId}`)?.querySelector(".list-body");
                    if (dualListSelectListCard)
                    {
                        dualListSelectListCard.addEventListener("scroll", this.scrollHandler);
                    }
                }
                else if (this.propertyMetadata.InputType === "MultiSelect")
                {
                    this.sortOptions();
                }
            });

        if (this.propertyMetadata.InputType === "MultiSelect")
        {
            requestAnimationFrame(() =>
            {
                self.$metadataInputSelectService.onMultiSelectChange.pipe(takeUntil(this.destructor$))
                    .subscribe(id =>
                    {
                        if (id === `multi-select-${self.popupId}` && !Utilities.objectsEqual(self.selected ? (<MetadataKey[]>self.selected).sort() : self.selected, self.model ? (<MetadataKey[]>self.model).sort() : self.model))
                        {
                            self.fireValueChanged(self.selected);
                        }
                        self.optionSearch = "";
                    });

                self.$metadataInputSelectService.onMultiSelectScroll.pipe(takeUntil(this.destructor$))
                    .subscribe(e =>
                    {
                        if (e.name === `multi-select-${self.popupId}`)
                            self.infiniteScroll(e.event, self);
                    });
            });
        }

        this._density = this.$metadataService.getInputDensity(this.compact);
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
    }

    ngOnDestroy(): void
    {
        const dualListSelectListCard = document.querySelector(`#${this.dualListSelectId}`);
        if (!Utilities.IsNullOrUndefined(dualListSelectListCard))
        {
            const cardBody = dualListSelectListCard.querySelector(".list-body");
            if (cardBody)
            {
                cardBody.removeEventListener("scroll", this.scrollHandler);
            }
        }

        this.destructor$.next();
        this.destructor$.complete();
    }

    infiniteScroll(e, context): void
    {
        if (context.showLoadMore)
        {
            const list = e.currentTarget;
            const compareHeight = list.scrollHeight - list.offsetHeight;
            //Compare within +/-1 to handle for browser zoom
            if (Math.abs(Math.round(list.scrollTop) - compareHeight) <= 1)
            {
                context.loadMore();
            }
        }
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if ((this.unboundModel && this.model) || this.propertyMetadata.$$forceReload)
        {
            if (!Utilities.objectsEqual(this.unboundModel, this.model) || this.propertyMetadata.$$forceReload)
            {
                this.onModelChanged(this.model);
            }
        }
        const dependentValuesChanged = this.updateDependentValues();
        const filterValuesChanged = this.updateFilterValues();
        const lookupPropertyValuesChanged = this.updateLookupPropertyValue();
        const constantFilterValueChanged = this.updateConstantFilterValue();
        const requiredPropertyChanged = this.updateRequiredPropertyValue();
        if (
            dependentValuesChanged ||
            filterValuesChanged ||
            lookupPropertyValuesChanged ||
            constantFilterValueChanged ||
            requiredPropertyChanged
        )
        {
            if (lookupPropertyValuesChanged)
                //Force refresh of control text in case lookup values have changed
                this._selected = undefined;
            if (!this._loadingPromise)
                this.forceLoadSelect();
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.disableForRecalculate ||
            this.selectLoading ||
            !this._options ||
            this._options.length === 0;
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    this.onModelChanged(_changes[key].currentValue);
                    break;
            }
        }
    }

    disableForRecalculate: boolean = false;
    allowRecalculateSpinner: boolean = false;

    get showSpinner(): boolean
    {
        const showSpinnerValue = this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
        if (!showSpinnerValue)
        {
            let setFocus: boolean;
            if (this.disableForRecalculate)
                setFocus = true;
            this.disableForRecalculate = false;
            this.allowRecalculateSpinner = false;
            if (setFocus)
                setTimeout(() => document.getElementById(this.controlId)?.focus(), 50);
        }
        return this.allowRecalculateSpinner && showSpinnerValue;
    }

    //This is required so that the input can gain focus before being disabled in order to return to focus after a recalculate finishes
    onFocus(): void
    {
        if (!this.forCellEdit)
            this.setRecalculateStatus();
    }

    setRecalculateStatus(): void
    {
        if (this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
        {
            this.disableForRecalculate = true;
            this.allowRecalculateSpinner = true;
        }
    }

    get isInvalid(): boolean
    {
        if (this.isRequired)
        {
            const self = this;
            let isEmpty = false;
            if (Utilities.IsNullOrUndefined(this.selected))
            {
                isEmpty = true;
            }

            if (!this.isMultiSelect && !this.isDualList)
            {
                isEmpty = ArrayHelpers.none(self.options, x => x.Key === this.selected);
            }

            if (Array.isArray(this.selected))
            {
                isEmpty = ArrayHelpers.none(self.options, x => ArrayHelpers.any(<MetadataKey[]>this.selected, y => x.Key === y));
            }

            this.error = isEmpty ? `${this.Label} is Required` : "";
        }
        else
        {
            this.error = "";
        }

        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    get popupId(): string
    {
        let indexChunk = "";
        if (!Utilities.IsNullOrUndefined(this.index))
        {
            indexChunk = "-" + this.index;
        }
        return this.Name + indexChunk;
    }

    private onModelChanged(value: MetadataKey | MetadataKey[]): void
    {
        const self = this;
        if (this._loadingPromise)
        {
            this._loadingPromise.then(a =>
            {
                self.onModelChanged(value);
                return a;
            });
            return;
        }
        if (this.propertyMetadata.$$forceReload)
        {
            self._options = null;
            self._deselectList = [];
            self.loadSelect(true)
                .then(() =>
                {
                    self.selected = value;
                    self.onModelChanged(self.model);
                });
            self.propertyMetadata.$$forceReload = false;
            return;
        }
        this.unboundModel = Utilities.deepCopy(value);
        if (!self.isMultiSelect && !self.isDualList)
        {
            const change = value as MetadataKey;
            if (Utilities.IsNullOrUndefined(change) || MetadataKeyHelpers.isNullKey(change))
            {
                requestAnimationFrame(function (_timestamp)
                {
                    self.selected = null;
                });
            }
            else
            {
                const item = ArrayHelpers.first(this._options, x => x.Key === change.toString());
                if (!item)
                {
                    console.warn(
                        `${MetadataInputSelectComponent.name}${this.propertyMetadata.Name
                        }|onModelChanged|No KeyValue was found corresponding to ${JSON.stringify(value)}! setting to ${this.model
                        }`
                    );
                    requestAnimationFrame(function (_timestamp)
                    {
                        self.selected = self.model;
                    });
                }
                else
                {
                    requestAnimationFrame(function (_timestamp)
                    {
                        self.selected = item.Key;
                    });
                }
            }
        }
        else
        {
            const change = value as MetadataKey[];
            this.selected = change ? this.applySort(this._options.filter(x => change.indexOf(x.Key) > -1))
                .map(x => x.Key) : [];
        }
    }

    get isMultiSelect(): boolean
    {
        return this.$metadataInputTypeHelperService.isMultiSelect(this.propertyMetadata.InputType);
    }

    get isDualList(): boolean
    {
        return this.$metadataInputTypeHelperService.isDualList(this.propertyMetadata.InputType);
    }

    /**
     * When `this.isMultiSelect` evaluates as truthy, this property will return an array of `KeyValue`s,
     * otherwise it will be a single `KeyValue`.
     */
    get selected(): MetadataKey | MetadataKey[]
    {
        return this._selected;
    }

    set selected(value: MetadataKey | MetadataKey[])
    {
        if (this.isDualList)
        {
            this._selected = this.applySort(this._options.filter(x => (<MetadataKey[]>value).indexOf(x.Key) > -1)).map(m => m.Key);
            this._deselectList = [];
        }
        else
            this._selected = value;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get Density(): string
    {
        return this._density;
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get isRequired(): boolean
    {
        return (
            !this.isViewOnly &&
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    get options(): KeyValuePair<MetadataKey, string>[]
    {
        return this._options;
    }

    get useDualList(): boolean
    {
        return this.options ? this.options.length > RECOMMENDED_THRESHOLD : false;
    }

    get popupOpen(): boolean
    {
        return this._popupOpen;
    }

    set popupOpen(value: boolean)
    {
        this._popupOpen = value;
    }

    get optionSearch(): string
    {
        return this._optionSearch;
    }

    set optionSearch(value: string)
    {
        this._optionSearch = value;
    }

    private updateDependentValues(): boolean
    {
        const list = this.$metadataPropertyService.getDependentValuesForProperty(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!areEquivalent(this.dependentValues, list))
        {
            this.dependentValues = list;
            return true;
        }
        return false;
    }

    private updateLookupPropertyValue(): boolean
    {
        if (!this.propertyMetadata.LookupProperty)
        {
            return false;
        }
        const list = this.$metadataService.DependentModuleValue(
            this.propertyMetadata.LookupProperty,
            this.parent,
            this.metadataModel
        ) ?? [];
        if (
            (Utilities.IsNullOrUndefined(list) && Utilities.IsNullOrUndefined(this.lookupPropertyValues)) ||
            ObjectHelpers.stringifyCircular(this.lookupPropertyValues) !== ObjectHelpers.stringifyCircular(list)
        )
        {
            this.lookupPropertyValues = JSON.parse(ObjectHelpers.stringifyCircular(list));
            return true;
        }
        return false;
    }

    private updateConstantFilterValue(): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.ConstantFilter))
        {
            return false;
        }
        const currentFilter = this.$metadataService.EvaluateString(
            this.propertyMetadata.ConstantFilter,
            this.parent,
            this.metadataModel, null, true
        );
        if (!(this.constantFilter === currentFilter))
        {
            this.constantFilter = currentFilter;
            return true;
        }
        return false;
    }

    private updateRequiredPropertyValue(): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.RequiredProperty))
        {
            return false;
        }
        const currentRequiredPropertyValue = this.$metadataPropertyService.isRequired(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!(this.requiredPropertyValue === currentRequiredPropertyValue))
        {
            this.requiredPropertyValue = currentRequiredPropertyValue;
            return true;
        }
        return false;
    }

    private updateFilterValues(): boolean
    {
        const list = this.$metadataPropertyService.getFilterByValuesForProperty(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!areEquivalent(this.filterValues, list))
        {
            this.filterValues = list;
            return true;
        }
        return false;
    }

    private forceLoadSelect(clearCache: boolean = false): void
    {
        this._options = null;
        this._deselectList = [];
        this.loadSelect(clearCache);
    }

    ready: boolean = false;

    private loadSelect(clearCache: boolean = false): Promise<any>
    {
        const self = this;
        if (this._options)
        {
            return Promise.resolve();
        }
        if (this.selectLoading)
        {
            return this._loadingPromise;
        }
        this.selectLoading = true;
        this._loadingPromise = this.QueryLookupService(clearCache)
            .then(findSelected)
            .finally(() =>
            {
                this._loadingPromise = undefined;
                this.selectLoading = false;
            });
        return this._loadingPromise;
        function findSelected(result: KeyValuePair<MetadataKey, string>[]): void
        {
            if (!self.isMultiSelect && !self.isDualList)
            {
                const modelSingle = <MetadataKey>self.model;
                if (
                    !Utilities.IsNullOrUndefined(modelSingle) &&
                    !self.selected &&
                    !MetadataKeyHelpers.isNullKey(modelSingle)
                )
                {
                    // we have a model (field value) but don't have matching KeyValue object.
                    const first = result.find(x => self.areEqual(x.Key, <MetadataKey>self.model));
                    if (!first)
                    {
                        console.warn(
                            `${MetadataInputSelectComponent.name}|${self.propertyMetadata.Name
                            }|model: ${JSON.stringify(self.model)}|key not found in options!`
                        );
                        self.selected = self.model;
                    }
                    else
                    {
                        self.selected = first.Key;
                    }
                }
                else if (!modelSingle && self.isRequired && result && result.length === 1)
                {
                    // we don't have a model (field value) and we are a required field and there is only one option
                    self.selected = result[0].Key;
                }
            }
            else
            {
                // REVIEW - I haven't really thought this one through....
                const modelArray = <MetadataKey[]>self.model;
                if (!modelArray)
                {
                    self.selected = [];
                }
                else if (modelArray && !self.selected)
                {
                    self.selected = self.applySort(result).map(x => x.Key)
                        .filter(x => modelArray.indexOf(x) > -1);
                    self.ready = true;
                }
                /* else if () {} */
            }
        }
    }

    private QueryLookupService(clearCache: boolean = false): Promise<KeyValuePair<MetadataKey, string>[]>
    {
        const self = this;
        const lookupType = this.propertyMetadata.Lookup;
        const filterString = this.createOdataFilter();
        if (this.propertyMetadata.LookupProperty)
        {
            return Promise.resolve(
                onSuccess(
                    this.$metadataService.DependentModuleValue(
                        this.propertyMetadata.LookupProperty,
                        this.parent,
                        this.metadataModel
                    )
                )
            );
        }
        if (clearCache)
        {
            this.$cachedCodeLookupService.clearCache(lookupType, filterString);
        }
        /*
         * This needs to be an explicit check since filterString can be an empty string and that is a valid value.
         * null and undefined however MUST return the $q deferred
         */
        return Utilities.IsNullOrUndefined(filterString)
            ? Promise.resolve([])
            : this.$cachedCodeLookupService.Get(lookupType, filterString)
                .then(onSuccess, onError);
        function onSuccess(result: KeyValuePair<MetadataKey, string>[]): Array<KeyValuePair<MetadataKey, string>>
        {
            const distinctResults =
                result && result.filter
                    ? result.filter((x, i, s) =>
                    {
                        return s.indexOf(x) === i;
                    })
                    : result;
            self._options = self.$metadataInputSelectService.MergeSelectOptions(
                lookupType,
                distinctResults,
                self.propertyMetadata,
                self.metadataModel
            );
            if (self.isDualList)
            {
                if (!self.propertyMetadata["$sorted"])
                    self._options.forEach((x, i) => x["$sortedIndex"] = i);
            }

            self._deselectList = [];
            if (
                self._options?.length > 0 &&
                !self.isMultiSelect &&
                !self.isDualList &&
                !self.$metadataPropertyService.isRequired(self.propertyMetadata, self.metadataModel, self.parent)
            )
                self._options.unshift({
                    Key: null,
                    Value: "",
                });
            return result;
        }
        function onError(): Array<any>
        {
            return [];
        }
    }

    private createOdataFilter(): string
    {
        return this.$metadataPropertyService.createOdataFilter(this.propertyMetadata, this.metadataModel, this.parent);
    }

    fireValueChanged($event: any): void
    {
        if (this.showSpinner)
        {
            $event.preventDefault();
            $event.stopPropagation();
            return;
        }
        const selected = this.isMultiSelect || this.isDualList ? $event : $event.target.value;

        // REVIEW - If a Select field isn't required, should we insert an NONE option into the control?
        if (this.isRequired && Utilities.IsNullOrUndefined(selected))
        {
            return;
        }
        const areSame = this.isMultiSelect || this.isDualList
            ? this.areAllEqual(<MetadataKey[]>selected, <MetadataKey[]>this.model)
            : this.areEqual(<MetadataKey>selected, <MetadataKey>this.model);
        if (areSame || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: selected });
    }

    get selectedCount(): string
    {
        const selectCount = (this.selected as MetadataKey[])?.length ?? 0;

        if (selectCount === 1)
        {
            const selected = this._options.filter(x => x.Key === this.selected[0]);
            return selected[0].Value;
        }
        if (selectCount > 1)
        {
            return `${selectCount} options selected`;
        }

        return "";
    }

    get targetId(): string
    {
        return `mlt-${this.Name}`;
    }

    get popupElementId(): string
    {
        return `multi-select-${this.Name}`;
    }

    get dualListSelectId(): string
    {
        return `${this.Name}DualSelectListCard`;
    }

    get multiSelectListSelectId(): string
    {
        return `${this.popupId}MultiSelectListCard`;
    }

    selectAllText(deselect: boolean): string
    {
        let appendFiltered = this.optionSearch.length > 0 ? " (filtered)" : "";
        return deselect ? `Deselect all${appendFiltered}` : `Select all${appendFiltered}`;
    }

    private selectListSelections: MetadataKey[] = [];
    deselectListSelections: MetadataKey[] = [];

    selectAll(): void
    {
        if (this.optionSearch.length > 0)
        {
            this.selected = (this.selected as MetadataKey[]).concat(
                this._options
                    .filter(x => x.Value.toLowerCase().includes(this.optionSearch.toLowerCase()))
                    .map(x => x.Key)
                    .filter(x => !(this.selected as MetadataKey[]).includes(x))
            );
        }
        else
        {
            this.selected = this._options.map(x => x.Key);
        }

        if (!this.isMultiSelect)
            this.addSelections();
    }

    deselectAll(): void
    {
        if (this.optionSearch.length > 0)
        {
            const self = this;
            const filteredItems = this._options
                .filter(x => x.Value.toLowerCase().includes(this.optionSearch.toLowerCase()))
                .map(x => x.Key);

            filteredItems.forEach(x =>
            {
                const i = (self.selected as MetadataKey[]).indexOf(x);
                if (i >= 0)
                {
                    (self.selected as MetadataKey[]).splice(i, 1);
                }
            });
        }
        else
        {
            this.selected = [];
        }
        if (this.isDualList)
            this.fireValueChanged(this.selected);
    }

    addSelections(): void
    {
        const selected: any = this.selected;
        this.selected = this.applySort(this._options.filter(x => this.selectListSelections.indexOf(x.Key) > -1 && selected.indexOf(x.Key) === -1))
            .map(x => x.Key)
            .concat(this.selected);
        this.fireValueChanged(this.selected);
        this.selectListSelections = [];
    }

    removeSelections(): void
    {
        const selected: any = this.selected;
        this.selected = this.applySort(this._options.filter(x => this.deselectListSelections.indexOf(x.Key) === -1 && selected.indexOf(x.Key) > -1))
            .map(x => x.Key);
        this.fireValueChanged(this.selected);
        this.deselectListSelections = [];
    }

    selectItem(item: MetadataKey): void
    {
        if (!this.isViewOnly)
        {
            if (this.isMultiSelect)
            {
                const index = (this.selected as MetadataKey[]).indexOf(item);
                if (index < 0)
                {
                    (this.selected as MetadataKey[]).push(item);
                }
                else
                {
                    (this.selected as MetadataKey[]).splice(index, 1);
                }
            }
            else
            {
                const index = this.selectListSelections.indexOf(item);
                if (index < 0)
                {
                    this.selectListSelections.push(item);
                }
                else
                {
                    this.selectListSelections.splice(index, 1);
                }
            }
        }
    }

    deselectItem(item: MetadataKey): void
    {
        const index = this.deselectListSelections.indexOf(item);
        if (index < 0)
        {
            this.deselectListSelections.push(item);
        }
        else
        {
            this.deselectListSelections.splice(index, 1);
        }
    }

    isItemChecked(item: MetadataKey): boolean
    {
        return (this.selected as MetadataKey[])?.indexOf(item) >= 0;
    }

    loadMore(): void
    {
        this.loadMoreCount++;
    }

    get showLoadMore(): boolean
    {
        if (Utilities.IsNullOrUndefined(this._options))
        {
            return false;
        }
        else
        {
            if (this.isMultiSelect)
            {
                return (
                    this._options.filter(x => x.Value.toLowerCase()
                        .includes(this.optionSearch.toLowerCase())).length > MAX_RECORDS_RENDERED * this.loadMoreCount
                );
            }
            else
            {
                return (
                    this._options.filter(x => x.Value.toLowerCase()
                        .includes(this.optionSearch.toLowerCase())).length - this.deselectList.length >
                    MAX_RECORDS_RENDERED * this.loadMoreCount
                );
            }
        }
    }

    sortOptions(): void
    {
        this._options = this._options
                        .sort((a,b) => this.isItemChecked(b.Key) && !this.isItemChecked(a.Key) ? 1 : !this.isItemChecked(b.Key) && this.isItemChecked(a.Key) ? -1 : 0 || a.Value.localeCompare(b.Value));
    }

    get selectList(): KeyValuePair<MetadataKey, string>[]
    {
        if (!this._options)
            return [];

        if (this.isMultiSelect)
        {
            if (this.optionSearch.length > 0)
            {
                return this._options
                    .filter(x => x.Value.toLowerCase()
                        .includes(this.optionSearch.toLowerCase()))
                    .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
            }
            else
            {
                return this._options
                    .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
            }
        }
        else
        {
            const selected: any = this.selected;
            if (selected)
            {
                if (this.optionSearch.length > 0)
                {
                    return this._options
                        .filter(x => selected.indexOf(x.Key) === -1)
                        .filter(x => x.Value.toLowerCase()
                            .includes(this.optionSearch.toLowerCase()))
                        .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
                }
                else
                {
                    return this._options
                        .filter(x => selected.indexOf(x.Key) === -1)
                        .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
                }
            }
            else
            {
                return this._options;
            }
        }

    }

    _deselectList: KeyValuePair<MetadataKey, string>[] = [];
    get deselectList(): KeyValuePair<MetadataKey, string>[]
    {
        if (this._deselectList.length > 0)
            return this._deselectList;
        const selected: any = this.selected;
        if (!Utilities.IsNullOrUndefinedOrEmpty(selected) && !Utilities.IsNullOrUndefinedOrEmpty(this._options))
        {
            this._deselectList = this.applySort(this._options.filter(x => selected.indexOf(x.Key) > -1));
            return this._deselectList;
        }
        else
        {
            return [];

        }
    }

    applySort(unsorted: KeyValuePair<MetadataKey, string>[]): KeyValuePair<MetadataKey, string>[]
    {
        if (this.propertyMetadata.InputType === "DualList")
            return  unsorted.sort((a, b) => a["$sortedIndex"] < b["$sortedIndex"] ? -1 : 1);
        return unsorted;
    }

    clearOptionSearch(): void
    {
        this.optionSearch = "";
    }

    private areEqual(a: MetadataKey, b: MetadataKey): boolean
    {
        const av = !Utilities.IsNullOrUndefined(a);
        const bv = !Utilities.IsNullOrUndefined(b);
        if (!av && !bv)
        {
            return true;
        }
        if (!av || !bv)
        {
            return false;
        }
        return a.toString() === b.toString();
    }

    private areAllEqual(a: MetadataKey[], b: MetadataKey[]): boolean
    {
        if (!a && !b)
        {
            return true;
        }
        if (!a || !b)
        {
            return false;
        }
        return a.map(x => x.toString()) === b.map(x => x.toString());
    }

    getButtonType(buttonType: string): string
    {
        const dense = this.compact ? "-dense" : "";
        return `${buttonType}${dense}`;
    }

    onKeyDown(event: KeyboardEvent): void
    {
        if(event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }
}
